import { BCTabContentWithScroll, BCTabItemWithIcon, BCTabs, ITabsItem } from '@bimser/components';
import { FormItemTypes, IDictionary, isNullOrUndefined, MLHelper } from '@bimser/core';
import IconEmpty from '@bimser/icons/16/empty-icon';
import IconHome from '@bimser/icons/16/home';
import GetIcon16ByName from '@bimser/icons/lib/getIcon16ByName';
import React, { JSX } from 'react';
import { IPropertyField, ITabItem, MultiLanguageText, Position } from '../../../../entities/common';
import { setPanelChildren } from '../../../../helper';
import ContainerControl from '../../base/ContainerControl';
import Tab from '../Tab';
import Styles from './assets/tabsStyles.scss';
import { ITabsProperties } from './entities/ITabsProperties';

export default class Tabs extends ContainerControl {
	items: ITabItem<MultiLanguageText>[];
	selectedIndex: number;
	selectedItem: ITabItem<IDictionary<string>>;
	tabPosition: Position;
	hasScrollbar: boolean;

	constructor() {
		super();

		this.controlType = FormItemTypes.EControlTypes.Tabs;
		this.caption.visible = false;
		this.items = [];
		this.tabPosition = 'top';
		this.hasScrollbar = true;
		this.style.height = 150;

		this.renderTabContent = this.renderTabContent.bind(this);

		this.subscribe('trigger:OnValueChanged', (value: string) => {
			this.onChange(value);
		});
	}

	__getMLClassPath() {
		return [...super.__getMLClassPath(), 'items.__array__.title'];
	}

	__loadProperties(data: ITabsProperties) {
		super.__loadProperties(data);

		this.__proxyChanged.selectedIndex = data.selectedIndex;
		this.__proxyChanged.selectedItem = data.selectedItem;
		this.__proxyChanged.tabPosition = data.tabPosition;
		this.__proxyChanged.hasScrollbar = data.hasScrollbar;

		if (data?.items?.length) {
			const newItems: ITabItem<MultiLanguageText>[] = [];

			data.items.forEach(item => {
				const newItem: ITabItem<MultiLanguageText> = {
					...item,
					title: new MultiLanguageText()
				};

				newItem.title.setData(item.title);
				newItems.push(newItem);
			});

			this.__proxyChanged.items = newItems;
		} else if (data?.items) {
			this.__proxyChanged.items = [];
		}
	}

	private getTabItems(): { data: ITabsItem[]; selectedKey: string } {
		let data: ITabsItem[] = [
			{
				key: 'tab1',
				label: BCTabItemWithIcon({ label: 'Tab1', icon: IconHome.info }),
				style: this.__getTabStyles().tabStyles,
				children: this.hasScrollbar
					? BCTabContentWithScroll({ children: this.renderTabContent('tab1') })
					: this.renderTabContent('tab1')
			},
			{
				key: 'tab2',
				label: BCTabItemWithIcon({ label: 'Tab2', icon: IconHome.info }),
				style: this.__getTabStyles().tabStyles,
				children: this.hasScrollbar
					? BCTabContentWithScroll({ children: this.renderTabContent('tab2') })
					: this.renderTabContent('tab2')
			}
		];

		const hasChild = this.__childrenItem?.result?.count?.();

		let selectedKey = 'tab1';
		if (hasChild) {
			const rootIds = this.__childrenItem.result;
			const rootItems = this.__childrenItem.entities.items.filter(i =>
				rootIds.contains(i.id)
			);
			if (rootItems?.count()) {
				data = [];
				rootItems
					.sort((a, b) => {
						const tabItemA = this.__form.items.get(a.id);
						const tabItemB = this.__form.items.get(b.id);
						return (
							tabItemA.properties.get('orderNo') - tabItemB.properties.get('orderNo')
						);
					})
					.forEach(item => {
						const tabItem = this.__form.items.get(item.id);
						const visible =
							tabItem.properties.get('visible') &&
							tabItem.properties.get('clientVisible');
						if (visible === false) {
							return;
						}

						const selected =
							(this.selectedItem
								? this.selectedItem?.key === item.id
								: tabItem.properties.get('selected')) || false;

						const label = this.items
							.find(tab => tab.key === tabItem.id)
							?.title?.getData?.();

						data.push({
							key: item.id,
							disabled: !tabItem.properties.get('enabled'),
							label: BCTabItemWithIcon({
								label: MLHelper.getMLText(label),
								icon: GetIcon16ByName(tabItem.properties.get('icon'), IconEmpty)
									.info
							}),
							style: this.__getTabStyles().tabStyles,
							children: this.hasScrollbar
								? BCTabContentWithScroll({
										children: this.renderTabContent(item.id)
								  })
								: this.renderTabContent(item.id)
						});
						if (selected) {
							selectedKey = item.id;
						}
					});
			}
		}

		return {
			data,
			selectedKey
		};
	}

	__getPropertyFields(): IPropertyField[] {
		const fields = super.__getPropertyFields();
		return [
			...fields,
			{
				name: 'selectedIndex',
				type: 'number',
				common: true
			},
			{
				name: 'selectedItem',
				type: 'object',
				items: [
					{
						name: 'selected',
						type: 'boolean'
					},
					{
						name: 'icon',
						type: 'string'
					},
					{
						name: 'orderNo',
						type: 'number'
					},
					{
						name: 'enabled',
						type: 'boolean'
					},
					{
						name: 'key',
						type: 'string',
						readOnly: true,
						common: true
					}
				]
			},
			{
				name: 'tabPosition',
				type: 'enum',
				enumMembers: ['bottom', 'left', 'right', 'top'] as Position[]
			}
		];
	}

	private __getTabStyles() {
		let tabStyles: React.CSSProperties = {};
		let tabBarStyles: React.CSSProperties = {};

		if (this.hasScrollbar) {
			if (this.style) {
				let height: number = this.style.height as number;
				if (this.tabPosition === 'top' || this.tabPosition === 'bottom') {
					height = (this.style.height as number) - 60;
					tabBarStyles = { height: 60 };
				}

				tabStyles = {
					...this.style,
					height
				};
			}
		} else if (this.style.height) {
			tabStyles = {
				...this.style,
				height: '',
				minHeight: 'calc(' + this.style.height + 'px - 50px)'
			};
		}

		return {
			tabStyles,
			tabBarStyles
		};
	}

	renderTabContent(key: string) {
		const rootIds = this.__childrenItem.result;
		const rootItems = this.__childrenItem.entities.items.filter(i => rootIds.contains(i.id));

		if (rootItems?.count()) {
			const itemStructure = rootItems.get(key);
			const itemData = this.__form.items.get(key);

			if (itemData) {
				const newTab = new Tab();
				const mlTitle = new MultiLanguageText(
					(text: string, culture: string, existCultures: string[]) => {
						(newTab as any).__onChangeMLTextValue(
							text,
							'__title',
							culture,
							existCultures
						);
					}
				);
				const title = itemData.getIn(['properties', 'title']);
				Object.keys(title).forEach(key => {
					mlTitle[key] = title[key];
				});
				mlTitle.__data = title;
				newTab.__form = this.__form;
				newTab.name = itemData.id;
				newTab.style = { ...this.style };
				newTab.icon = itemData.getIn(['properties', 'icon']);
				newTab.selected = itemData.getIn(['properties', 'selected']);
				newTab.orderNo = itemData.getIn(['properties', 'orderNo']);
				newTab.key = itemData.getIn(['properties', 'key']);
				newTab.__title = mlTitle;
				newTab.readOnly = this.__isReadOnly() || itemData.getIn(['properties', 'readOnly']);
				newTab.enabled = itemData.getIn(['properties', 'enabled']);
				newTab.clientEnabled = itemData.getIn(['properties', 'clientEnabled']);

				if (this.style.height) {
					newTab.style = {
						...this.style,
						height: '',
						minHeight: 'calc(' + this.style.height + 'px - 50px)'
					};
				}

				// if there is no selected item, assume first selected
				const nothingSelected =
					this.controlType === FormItemTypes.EControlTypes.Tabs &&
					this.items.every(i => !i.selected);
				const isFirstOrder = newTab.orderNo === 0;

				if (
					itemData.getIn(['properties', 'selected']) ||
					this.controlType !== FormItemTypes.EControlTypes.Tabs ||
					(nothingSelected && isFirstOrder)
				) {
					newTab.__loadChildren(
						setPanelChildren(itemStructure, this.prepareRenderOrderProperties()) as any
					); // Set tab children
					return newTab.__render();
				}
			}
		}
	}

	protected onChange(activeKey: string) {
		const newItems = this.items.map(item => ({ ...item, selected: item.key == activeKey }));
		const selectedItem = newItems.find(item => item.selected);

		this.__proxyChanging.items = newItems;
		this.__proxyChanging.selectedItem = selectedItem;
		this.__proxyChanging.selectedIndex = selectedItem ? selectedItem.orderNo : 0;
	}

	__getComponentData(): IDictionary<any> {
		return {
			...super.__getComponentData(),
			items: this.items,
			selectedIndex: this.selectedIndex,
			selectedItem: this.selectedItem,
			tabPosition: this.tabPosition,
			hasScrollbar: this.hasScrollbar
		};
	}

	__render(children?: JSX.Element) {
		const selectedItem = isNullOrUndefined(this.selectedItem)
			? {}
			: { activeKey: this.selectedItem.key };

		if (children) {
			return super.__render(children);
		}
		const { data, selectedKey } = this.getTabItems();
		return super.__render(
			<BCTabs
				items={data}
				tabPosition={this.__form.__fromMobile ? 'top' : this.tabPosition}
				tabBarStyle={this.__getTabStyles().tabBarStyles}
				onChange={this.onChange.bind(this)}
				cssClass={Styles.tabs}
				defaultActiveKey={selectedKey}
				{...selectedItem}
				style={{
					...{
						...(this.hasScrollbar
							? {
									height: this.style.height
										? 'calc(' + this.style.height + 'px + 50px)'
										: '150px'
							  }
							: { height: '' })
					},
					...{ '--bgcolor': this.style.backgroundColor },
					...{
						...(!this.hasScrollbar && this.style.height
							? { minHeight: this.style.height }
							: { minHeight: '' })
					}
				}}
			/>
		);
	}
}
